"use client";

import { ListBox } from "@ui/components/core/ListBox";
import { Button, RouterProvider, Select } from "react-aria-components";
import { usePageFooterData } from "./PageFooterContext";
import { useMemo } from "react";
import { RELEASED_TARGET_LANGUAGES, type Locale } from "@mw/shared/i18n";
import { getDynamicFlagURL } from "@ui/components/languages/DynamicFlag";
import { recordLanguageDecision } from "../LanguageChangeBanner";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { ttsLanguagesMap as languagesMap } from "@components/languages/config";
import { LOCALE_REGION_SUBLABELS, LOCALIZED_LABELS } from "../languageUtils";
import { useLocale, useTranslations } from "next-intl";
import { clsx } from "clsx";
import IconChevronDownSmall from "@ui/central-icons/chevron-down-small.svg";
import IconTranslate from "@ui/central-icons/translate.svg";

function getLocaleFromPath(path: string, locales: Locale[]): Locale {
  const queryIndex = path.indexOf("?");
  const pathWithoutQuery = queryIndex === -1 ? path : path.slice(0, queryIndex);
  const locale = pathWithoutQuery.split("/")[1];
  if (locale && locales.includes(locale as Locale)) {
    return locale as Locale;
  }
  return "en";
}

export function LanguageSelectorTrigger({ isOpen = false }: { isOpen?: boolean }) {
  const currentLocale = useLocale();
  const currentLanguage = languagesMap[currentLocale];
  const currentLanguageName = currentLanguage?.label ?? "Language";

  return (
    <Button className='tw-group tw-text-black tw-font-medium tw-text-sm/none tw-px-1.5 tw-h-7 tw-flex tw-items-center tw-outline-none focus-visible:tw-outline-focus tw-rounded'>
      <IconTranslate aria-hidden className='tw-size-5 tw-flex-none tw-mr-1.5' />
      {currentLanguageName}
      <IconChevronDownSmall
        aria-hidden
        className={clsx(
          "tw-mt-0.25 tw-ml-2 tw-size-4 tw-flex-none tw-transition",
          isOpen ? "tw-text-black" : "tw-text-gray-500 group-hover:tw-text-black group-focus-visible:tw-text-black",
        )}
      />
    </Button>
  );
}

export function LanguageSelector() {
  const t = useTranslations("ClientKeys.Components.LanguagePicker");
  const { enabledLocales, missingLocaleBehavior } = usePageFooterData();
  const currentLocale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams?.toString() ?? "");
  const urlParams = params.size > 0 ? `?${params.toString()}` : "";
  const nextUrl = (locale: Locale) =>
    locale === "en" ? `${pathname}${urlParams}` : `/${locale}${pathname === "/" ? "" : pathname}${urlParams}`;

  const languageOptions = useMemo(() => {
    // If enabledLocales is set, filter to only show those locales
    // Otherwise, show all released locales
    if (missingLocaleBehavior !== "no-index" && enabledLocales && enabledLocales.length > 0) {
      // Ensure 'en' is always first if it's enabled
      const sortedLocales = [...enabledLocales].sort((a, b) => {
        if (a === "en") return -1;
        if (b === "en") return 1;
        return 0;
      });
      return sortedLocales as Locale[];
    }

    // Default: show all released target languages
    return ["en", ...RELEASED_TARGET_LANGUAGES] as Locale[];
  }, [enabledLocales, missingLocaleBehavior]);

  return (
    <RouterProvider
      navigate={path => {
        const locale = getLocaleFromPath(path, languageOptions);
        recordLanguageDecision(locale);
        router.replace(`${pathname}${urlParams}`, { locale });
      }}
    >
      <Select value={currentLocale} aria-label={t("select_language")}>
        {({ isOpen }) => (
          <>
            <LanguageSelectorTrigger isOpen={isOpen} />
            <ListBox
              crossOffset={-8}
              items={languageOptions.map(locale => {
                const language = languagesMap[locale];
                const localizedLanguage = LOCALIZED_LABELS[locale];
                return {
                  id: locale,
                  textValue: localizedLanguage,
                  subLabel:
                    LOCALE_REGION_SUBLABELS[locale] ??
                    (language.label !== localizedLanguage ? language.label : undefined),
                  image: { src: getDynamicFlagURL(locale), width: 16, height: 16 },
                  link: { href: nextUrl(locale) },
                };
              })}
            />
          </>
        )}
      </Select>
    </RouterProvider>
  );
}
